import React, { FC, ReactNode, useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Divider, Flex } from "@evergis/ui";

import { ApplyLabelContainer, CardBlock } from "./styled";
import { CardAdditionalBlock } from "./CardAdditionalBlock";

import { useResourceType } from "../../../../hooks/useResourceType";
import { useSelectedResource } from "../../hooks/useSelectedResource";
import { usePreviewSrc } from "../../hooks/usePreviewSrc";
import { getAnySelected, getCategory } from "../../../../ducks/catalog/selectors";
import { __systemNameLogger } from "../../../../utils/logger";
import { getCardFallback } from "./utils";

import { ResourceCategory } from "../../hooks/useFetchParams";
import { ResourceInfoDc } from "../../types";

interface CardContentProps {
  resource: ResourceInfoDc;
  children: ReactNode;
  applyLabel: ReactNode;
  disabled?: boolean;
  preview?: string;
  onOpen: (card: ResourceInfoDc) => void;
  titlePrefix?: ReactNode;
}

export const CardContent: FC<CardContentProps> = ({
  resource,
  children,
  applyLabel,
  onOpen,
  disabled,
  preview,
  titlePrefix,
}) => {
  const { t } = useTranslation("catalog");
  const [resourceType] = useResourceType();
  const currentCategory = useSelector(getCategory(resourceType));
  const anyResourceSelected = useSelector(getAnySelected);
  const [selected, toggleSelected] = useSelectedResource(resource);
  const { name, alias, owner, preview: resourcePreview } = resource;
  const previewSrc = usePreviewSrc(preview, resourcePreview);
  const [currentSrc, setCurrentSrc] = useState(previewSrc);

  const titleOwner = useMemo(
    () =>
      [ResourceCategory.Shared, ResourceCategory.Public].includes(currentCategory) && owner
        ? `${t("owner")}: ${owner}`
        : null,
    [currentCategory, owner, t],
  );

  const processedTitle = useMemo(
    () =>
      titlePrefix && titleOwner ? (
        <Flex>
          {titlePrefix} <Divider vertical style={{ margin: "0 0.5rem" }} /> {titleOwner}
        </Flex>
      ) : (
        <>{titlePrefix || titleOwner}</>
      ),
    [titleOwner, titlePrefix],
  );

  const fallback = getCardFallback(resource, resourceType);

  const onImageLoadError = useCallback(() => {
    setCurrentSrc(fallback);
  }, [fallback]);

  return (
    <CardBlock
      checkboxChecked={selected}
      checkbox={{
        onChange: toggleSelected,
        checked: selected,
        name: "card-chb",
        value: "checkedCard",
      }}
      imageSource={currentSrc}
      imageProps={{ onError: onImageLoadError }}
      fallback={fallback}
      header={alias || name}
      title={processedTitle}
      imageGradient={!!previewSrc && currentSrc !== fallback}
      additionalBlock={<CardAdditionalBlock resource={resource} />}
      imageHeight={"48.5%"}
      width={"inherit"}
      height={"inherit"}
      onClick={__systemNameLogger(name)}
      apply={{
        label: <ApplyLabelContainer>{applyLabel}</ApplyLabelContainer>,
        disabled: disabled || anyResourceSelected,
        onClick: () => onOpen(resource),
      }}
    >
      {children}
    </CardBlock>
  );
};
